import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, ChevronDown } from 'lucide-react'
import { Container } from './Container'
import { MobileMenu } from './MobileMenu'
import { LanguageSwitcher } from '../ui/LanguageSwitcher'
import { Button } from '../ui/Button'
import { cn } from '../../lib/utils'
import type { NavItem } from '../../types'

export function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)

  const platformChildren = [
    { label: t('header.brain'), href: '/plataforma/brain' },
    { label: t('header.build'), href: '/plataforma/build' },
    { label: t('header.govern'), href: '/plataforma/govern' },
  ]

  const navItems: NavItem[] = [
    {
      label: t('header.platform'),
      children: platformChildren,
    },
    { label: t('header.howWeWork'), href: '/how-we-work' },
  ]

  const isPlatformActive = platformChildren.some((child) => location.pathname === child.href)

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 border-b border-border/50 bg-bg/80 backdrop-blur-xl">
        <Container size="wide">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-xl font-semibold text-gradient">
              <span>EmpleaHoy IA</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden items-center gap-8 lg:flex">
              {navItems.map((item) => {
                const isActive = item.children
                  ? isPlatformActive
                  : item.href
                    ? item.href.startsWith('/#')
                      ? location.hash === item.href.replace('/', '')
                      : location.pathname === item.href
                    : false

                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => item.children && setActiveDropdown(item.label)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    {item.href ? (
                      <a
                        href={item.href}
                        className={cn(
                          'flex items-center gap-1 text-sm font-medium transition-colors',
                          isActive
                            ? 'text-accent-light'
                            : 'text-text-secondary hover:text-text-primary',
                        )}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <button
                        type="button"
                        className={cn(
                          'flex items-center gap-1 text-sm font-medium transition-colors',
                          isActive
                            ? 'text-accent-light'
                            : 'text-text-secondary hover:text-text-primary',
                        )}
                        aria-expanded={activeDropdown === item.label}
                      >
                        {item.label}
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    )}

                    <AnimatePresence>
                      {item.children && activeDropdown === item.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.15 }}
                          className="absolute left-0 top-full mt-2 w-56 rounded-xl border border-border-subtle bg-surface-elevated p-2 shadow-xl"
                        >
                          {item.children.map((child) => {
                            const isChildActive = location.pathname === child.href
                            return (
                              <Link
                                key={child.label}
                                to={child.href ?? ''}
                                className={cn(
                                  'block rounded-lg px-4 py-2.5 text-sm transition-colors',
                                  isChildActive
                                    ? 'bg-surface text-accent-light'
                                    : 'text-text-secondary hover:bg-surface hover:text-text-primary',
                                )}
                                aria-current={isChildActive ? 'page' : undefined}
                              >
                                {child.label}
                              </Link>
                            )
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })}
            </nav>

            <div className="hidden items-center gap-4 lg:flex">
              <LanguageSwitcher />
              <Link
                to="/admin"
                className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary"
              >
                {t('header.admin')}
              </Link>
              <Button asChild size="md">
                <a href="/#diagnostico">{t('header.cta')}</a>
              </Button>
            </div>

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              className="rounded-full p-2 text-text-secondary hover:bg-surface-elevated lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </Container>
      </header>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navItems={navItems}
        ctaLabel={t('header.cta')}
        ctaHref="/#diagnostico"
      />
    </>
  )
}
